import { List, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import useNetworkType from '@/hooks/useNetworkType'
import { useAuth } from '@/plugins/auth'
import { REPORT_BUG } from '@/utils/utils'

import AddressDisplay from '../User/AddressDisplay'
import { ToggleDetails } from './StartAirdrop'
import { AIRDROP_APPEAL_FORM } from './utils'

interface NeedHelpProps {
  bot?: boolean
  assistance?: boolean
  address?: boolean
  review?: boolean
  onChainRewards?: boolean
}

interface NeedHelpType {
  bot: boolean
  assistance: boolean
  address: boolean
  review: boolean
  onChainRewards: boolean
}

const NeedHelp: React.FC<NeedHelpProps> = ({
  bot = false,
  assistance = false,
  address = false,
  review = false,
  onChainRewards = false,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { isBetanet } = useNetworkType()
  const { address: myAddress } = useAuth()
  const [openMore, setOpenMore] = useState<NeedHelpType>({
    bot: false,
    assistance: false,
    address: false,
    review: false,
    onChainRewards: false,
  })

  const toggleOpenMore = useCallback((key: keyof typeof openMore) => {
    setOpenMore(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  if (!bot && !assistance && !address && !review && !onChainRewards) {
    return null
  }

  return (
    <Stack style={{ gap: '5px' }}>
      <Title order={4}>{t('components.airdrop.help', 'Need Help?')}</Title>
      {bot && (
        <ToggleDetails
          title={t('components.airdrop.bot.title', 'How to interact with the airdrop bot')}
          onClick={() => toggleOpenMore('bot')}
          open={openMore.bot}
        >
          <List type="ordered" style={{ marginLeft: 0, marginRight: '20px' }}>
            <List.Item>
              <Text style={{ fontSize: 14, wordBreak: 'break-all' }}>
                {t(
                  'components.airdrop.interactInstructionsSteps1',
                  'Read the instructions, then submit the command ‘/airdrop’ into the channel. Wait a moment. Let the bot populate into your command line, and then press enter.'
                )}
              </Text>
            </List.Item>
            <List.Item>
              <Text style={{ fontSize: 14, wordBreak: 'break-all' }}>
                {t(
                  'components.airdrop.interactInstructionsStep2',
                  'If properly typed, inputting this command will pull up a new bot modal you can click on and interact with to find your rewards, link your Discord/community rewards to your Mainnet Hub account, transfer your platform rewards from Betanet over to a new account, and begin the claim process that will be transferred over to the Hub.'
                )}
              </Text>
            </List.Item>
          </List>
        </ToggleDetails>
      )}
      {assistance && (
        <ToggleDetails
          title={t('components.airdrop.assistance.title', 'Where to get assistance with claiming')}
          onClick={() => toggleOpenMore('assistance')}
          open={openMore.assistance}
        >
          <Trans i18nKey="components.airdrop.bot.description">
            <Text style={{ fontSize: 14 }}>
              For live assistance, send a message to our moderators on the{' '}
              <a
                href="https://discord.com/channels/981694584474181654/1250916510629761145"
                style={{ wordBreak: 'keep-all' }}
              >
                #airdrop-support
              </a>{' '}
              channel on Discord.
            </Text>
          </Trans>
          <Trans i18nKey="components.airdrop.assistance.description">
            <Text style={{ fontSize: 14 }}>
              You can also submit an error report via the feedback form{' '}
              <a href={REPORT_BUG} target="_blank" rel="noreferrer">
                here
              </a>
              .
            </Text>
          </Trans>
        </ToggleDetails>
      )}
      {address && (
        <ToggleDetails
          title={t('components.airdrop.address.title', 'Update your address')}
          onClick={() => toggleOpenMore('address')}
          open={openMore.address}
        >
          <Text style={{ fontSize: 14 }}>
            {t(
              'components.airdrop.address.description',
              'Make sure you’re claiming your rewards with the Mainnet account(s) you were most active on from April 26-October 24, 2024. Note that this airdrop is not designed to reward Betanet or Testnet activity.'
            )}
          </Text>
        </ToggleDetails>
      )}
      {review && (
        <ToggleDetails
          title={t('components.airdrop.review.title', 'Request a review')}
          onClick={() => toggleOpenMore('review')}
          open={openMore.review}
        >
          <Text style={{ fontSize: 14 }}>
            <Trans i18nKey="components.airdrop.review.description">
              If you believe that your airdrop is not right based on your past activity, you can{' '}
              <a href={AIRDROP_APPEAL_FORM} target="_blank" rel="noreferrer">
                request a review
              </a>{' '}
              of your airdrop allocation. Airdrop Appeals will be reviewed by the LAMINA1 core team
              throughout the airdrop claim period.
            </Trans>
          </Text>
        </ToggleDetails>
      )}
      {onChainRewards && !isBetanet && (
        <ToggleDetails
          title={t('components.airdrop.onChainRewards?', 'Where are my platform rewards?')}
          onClick={() => toggleOpenMore('onChainRewards')}
          open={openMore.onChainRewards}
        >
          <AddressDisplay
            showIdentity={false}
            address={myAddress as string}
            truncated={isMobile}
            tip={t(
              'components.airdrop.modal.confirmAddressTip',
              'This is the address linked to your Mainnet account'
            )}
          />
        </ToggleDetails>
      )}
    </Stack>
  )
}

export default NeedHelp
